import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';
import Button from '../../components/ui/Button';
import PmScopeFilterBar, { Field } from '../../components/performance/PmScopeFilterBar';
import usePmAnalyticsFilters, { EMPTY_FILTERS, latestPeriod, serializeFilters } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { getFeedbackAnalyticsSettings } from '../../services/performanceFeedbackAnalyticsService';
import { fetchPeriods, usePanel } from '../performance-operations/config';
import { Grid, PageEmpty } from '../performance-operations/styles';
import FeedbackKpis from './components/FeedbackKpis';
import MonthlyPanel from './components/MonthlyPanel';
import TopicsPanel from './components/TopicsPanel';
import UnitsPanel from './components/UnitsPanel';
import FeedbackInsightsPanel from './components/FeedbackInsightsPanel';
import RecordsTab from './components/RecordsTab';
import FeedbackSettingsDrawer from './components/FeedbackSettingsDrawer';
import {
  BADGES, CATEGORIES, EMPTY_FEEDBACK, SHARE_LEVELS, SOURCE_TYPES, feedbackParams, fetchBreakdown, fetchMonthly, fetchSummary,
  fetchTopics, parseFeedbackState, serializeFeedbackState,
} from './config';

// PM analitik P6 — E13 Sürekli geri bildirim analitiği (/performance/feedback-analytics; analiz §16). İK ve astı olan yöneticiler
// (kapsam sunucuda: dönemin katılımcı kümesi; İK kiracı, yönetici güncel alt ağacı). YALNIZ SAYILAR: geri bildirim metni,
// anonim gönderici ve 1:1 içeriği hiçbir yüzeyde yok; 1:1 sayıları kiracı ayarıyla ve ekip düzeyinde. Süzgeçler URL'de; panel
// başına bağımsız yükleme; tek kaydırma (Özet kendi alanında kayar, Kayıtlar sekmesinde yalnız tablo satırları).
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const Header = styled.div`
  flex-shrink: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 16px;
`;

const staticLookup = (keys, labelOf) => ({
  onSearch: async (q) => keys
    .map((k) => ({ id: k, name: labelOf(k) }))
    .filter((o) => !q || o.name.toLocaleLowerCase().includes(String(q).toLocaleLowerCase())),
  onGetById: async (id) => ({ id, name: labelOf(id) }),
});

export default function PerformanceFeedbackAnalyticsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const { isHr } = useAuth();
  const { filters, setFilters } = usePmAnalyticsFilters();
  const fb = useMemo(() => parseFeedbackState(location.search), [location.search]);
  const setFb = useCallback((patch) => {
    history.replace({ pathname: location.pathname, search: serializeFeedbackState({ ...fb, ...patch }, location.search) });
  }, [history, location.pathname, location.search, fb]);

  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tabHeight, setTabHeight] = useState(0);
  const tabRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    getFeedbackAnalyticsSettings().then((res) => { if (alive && res?.data?.status) setSettings(res.data.data); });
    return () => { alive = false; };
  }, []);

  // Dönem zorunlu (kapsam = dönemin katılımcı kümesi): URL'de yoksa en yeni dönem.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  useEffect(() => {
    const el = tabRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => entries.forEach((e) => setTabHeight(e.contentRect.height)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const params = useMemo(() => feedbackParams(filters, fb), [filters, fb]);
  const key = `${JSON.stringify(params)}|${refreshKey}`;
  const onSummary = fb.tab === 'summary';
  const summary = usePanel(() => fetchSummary(params), [key], ready && onSummary);
  const monthly = usePanel(() => fetchMonthly(params), [key], ready && onSummary);
  const topics = usePanel(() => fetchTopics(params), [key], ready && onSummary);
  const units = usePanel(() => fetchBreakdown(params, fb.dimension), [key, fb.dimension], ready && onSummary);

  const topicLabel = useCallback((k) => t(`performance.feedbackAnalytics.topic.${k}`), [t]);
  const topicLookup = useMemo(() => staticLookup([...CATEGORIES, ...BADGES], topicLabel), [topicLabel]);
  const sourceLookup = useMemo(() => staticLookup(SOURCE_TYPES, (k) => t(`performance.feedbackAnalytics.source.${k}`)), [t]);
  const shareLookup = useMemo(() => staticLookup(SHARE_LEVELS, (k) => t(`performance.feedbackAnalytics.share.${k}`)), [t]);

  // Çubuk → yetkili kayıt listesi (konu süzgeciyle; rozet ise takdir kayıtları).
  const onTopic = (topic, kind) => setFb({ topics: [topic], tab: 'records', recordType: kind === 'badge' ? 'recognition' : 'feedback' });
  // Birim/ekip satırı → ortak süzgeç şeridine eklenir (sayfa o kapsamla yeniden hesaplanır).
  const onUnit = (dimension, unitKey) => {
    if (dimension === 'department') setFilters((f) => ({ ...f, departmentIds: [unitKey] }));
    else setFilters((f) => ({ ...f, managerEmployeeId: unitKey, includeDescendants: false }));
  };
  const onReset = () => {
    const base = serializeFeedbackState({ ...EMPTY_FEEDBACK, tab: fb.tab, recordType: fb.recordType }, location.search);
    history.replace({ pathname: location.pathname, search: serializeFilters({ ...EMPTY_FILTERS, periodId: filters.periodId }, base) });
  };

  const extraChips = [
    ...(fb.from || fb.to ? [{ key: 'range', label: t('performance.feedbackAnalytics.chip.range', { from: fb.from || '…', to: fb.to || '…' }), onRemove: () => setFb({ from: null, to: null }) }] : []),
    ...(fb.topics.length ? [{ key: 'topics', label: `${t('performance.feedbackAnalytics.filter.topics')} (${fb.topics.length})`, onRemove: () => setFb({ topics: [] }) }] : []),
    ...(fb.sources.length ? [{ key: 'sources', label: `${t('performance.feedbackAnalytics.filter.sources')} (${fb.sources.length})`, onRemove: () => setFb({ sources: [] }) }] : []),
    ...(fb.shares.length ? [{ key: 'shares', label: `${t('performance.feedbackAnalytics.filter.shares')} (${fb.shares.length})`, onRemove: () => setFb({ shares: [] }) }] : []),
  ];

  const tabs = [
    { key: 'summary', label: t('performance.feedbackAnalytics.tab.summary') },
    { key: 'records', label: t('performance.feedbackAnalytics.tab.records') },
  ];

  return (
    <PageContainer>
      <Header>
        <TitleRow>
          <Breadcrumbs items={[{ label: t('performance.feedbackAnalytics.title') }]} />
          {isHr ? (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-action="settings">
              <Settings size={16} />
              {' '}
              {t('performance.feedbackAnalytics.settings.open')}
            </Button>
          ) : null}
        </TitleRow>
        {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.feedbackAnalytics.noPeriods')}</PageEmpty> : null}
        {periods.length > 0 ? (
          <PmScopeFilterBar
            filters={filters}
            onChange={setFilters}
            onReset={onReset}
            extraChips={extraChips}
            lastUpdated={summary.data?.calculatedAt}
            periodControl={(
              <Field $w="260px">
                <GenericSelect
                  label={t('performance.operations.filter.period')}
                  value={filters.periodId}
                  onChange={(v) => setFilters({ ...filters, periodId: v })}
                  options={periods.map((p) => ({ id: p.id, name: p.name }))}
                  idKey="id"
                  labelKey="name"
                  clearable={false}
                />
              </Field>
            )}
            extraControls={(
              <>
                <Field $w="170px">
                  <DateTimePicker label={t('performance.feedbackAnalytics.filter.from')} value={fb.from || ''} maxDate={fb.to || undefined}
                    onChange={(v) => setFb({ from: v || null })} />
                </Field>
                <Field $w="170px">
                  <DateTimePicker label={t('performance.feedbackAnalytics.filter.to')} value={fb.to || ''} minDate={fb.from || undefined}
                    onChange={(v) => setFb({ to: v || null })} />
                </Field>
                <Field>
                  <AsyncGenericSelect label={t('performance.feedbackAnalytics.filter.topics')} value={fb.topics} isMulti minChars={0} compact
                    onChange={(v) => setFb({ topics: v || [] })} {...topicLookup} />
                </Field>
                <Field $w="200px">
                  <AsyncGenericSelect label={t('performance.feedbackAnalytics.filter.sources')} value={fb.sources} isMulti minChars={0} compact
                    onChange={(v) => setFb({ sources: v || [] })} {...sourceLookup} />
                </Field>
                <Field $w="200px">
                  <AsyncGenericSelect label={t('performance.feedbackAnalytics.filter.shares')} value={fb.shares} isMulti minChars={0} compact
                    onChange={(v) => setFb({ shares: v || [] })} {...shareLookup} />
                </Field>
              </>
            )}
          />
        ) : null}
        {ready ? <Tabs tabs={tabs} activeTab={fb.tab} onChange={(tab) => setFb({ tab })} /> : null}
      </Header>
      <TabContent ref={tabRef}>
        {ready && fb.tab === 'summary' ? (
          <ScrollArea>
            <Grid>
              <FeedbackKpis panel={summary} />
              <MonthlyPanel panel={monthly} />
              <TopicsPanel panel={topics} onPick={onTopic} />
              <UnitsPanel panel={units} dimension={fb.dimension} onDimension={(d) => setFb({ dimension: d })} onPick={onUnit} />
              <FeedbackInsightsPanel panel={summary} />
            </Grid>
          </ScrollArea>
        ) : null}
        {ready && fb.tab === 'records' ? (
          <RecordsTab params={params} recordType={fb.recordType} onRecordType={(rt) => setFb({ recordType: rt })} height={tabHeight} isHr={isHr} />
        ) : null}
      </TabContent>
      {isHr ? (
        <FeedbackSettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settings}
          onSaved={(s) => { setSettings(s); setRefreshKey((k) => k + 1); }}
        />
      ) : null}
    </PageContainer>
  );
}
